import { useEffect, useState } from "react";
import { Star, BadgeCheck } from "lucide-react";
import { Reveal } from "./Reveal";
import { API } from "@/lib/api";
import { IMAGES } from "@/lib/images";

const SAMPLES = [
    {
        name: "Sophie L.",
        ctx: "Télétravail — Lyon",
        avatar: IMAGES.avatar1,
        rating: 5,
        text: "Trois semaines d’utilisation et mes douleurs au coccyx ont quasiment disparu. Je ne peux plus travailler sans mon Ergocox.",
    },
    {
        name: "Marc D.",
        ctx: "Chauffeur — Bordeaux",
        avatar: IMAGES.avatar2,
        rating: 5,
        text: "Je passe 6h par jour dans ma voiture. La découpe en U change tout : plus de douleur ni d’engourdissement en fin de journée.",
    },
    {
        name: "Élise R.",
        ctx: "Future maman — Nantes",
        avatar: null,
        initials: "ÉR",
        rating: 5,
        text: "Au 8e mois, s’asseoir était un calvaire. Ce coussin m’a sauvé le quotidien, au bureau comme à la maison.",
    },
];

function initialsOf(name) {
    return (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function Testimonials() {
    const [reviews, setReviews] = useState(null);

    useEffect(() => {
        fetch(`${API}/reviews`)
            .then((r) => r.json())
            .then((d) => setReviews(d.reviews || []))
            .catch(() => setReviews([]));
    }, []);

    const real = reviews && reviews.length > 0;
    const items = real ? reviews : SAMPLES;

    return (
        <section id="avis" className="py-20 lg:py-32 bg-sage-hover text-cream overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal className="max-w-2xl">
                    <p className="text-xs uppercase font-mono tracking-[0.25em] text-cream/50">
                        {real ? "Avis clients vérifiés" : "Ils se sont assis, ils ont souri"}
                    </p>
                    <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                        Des vies qui reprennent <span className="italic text-terra">place assise.</span>
                    </h2>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {items.map((r, i) => (
                        <Reveal key={`${r.name}-${i}`} delay={i * 0.12} className="h-full">
                            <figure
                                data-testid={`testimonial-${i}`}
                                className="h-full flex flex-col rounded-3xl border border-cream/15 bg-cream/5 p-6 sm:p-8 hover:border-terra/50 transition-colors duration-300"
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex gap-1 text-terra">
                                        {Array.from({ length: r.rating || 5 }).map((_, s) => (
                                            <Star key={s} size={15} fill="currentColor" strokeWidth={0} />
                                        ))}
                                    </div>
                                    {real && (
                                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-300/90" data-testid="review-verified-badge">
                                            <BadgeCheck size={13} /> Achat vérifié
                                        </span>
                                    )}
                                </div>
                                <blockquote className="mt-5 flex-1 font-serif italic text-lg lg:text-xl leading-relaxed text-cream/90">
                                    « {r.text || r.comment} »
                                </blockquote>
                                <figcaption className="mt-7 flex items-center gap-3">
                                    {r.avatar ? (
                                        <img src={r.avatar} alt={r.name} className="w-11 h-11 rounded-full object-cover border border-cream/20" />
                                    ) : (
                                        <span className="w-11 h-11 rounded-full bg-terra/25 text-cream grid place-items-center font-serif text-sm">
                                            {r.initials || initialsOf(r.name)}
                                        </span>
                                    )}
                                    <span>
                                        <span className="block text-sm font-semibold">{r.name}</span>
                                        <span className="block text-xs text-cream/60 font-mono">{r.ctx}</span>
                                    </span>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
